import clsx from 'clsx';

import type { RevisionTreePage } from '@gitbook/hive-core';
import { Icon } from '@gitbook/spine-icons';
import { type IconDefinition, renderIcon, Tooltip } from '@gitbook/spine-react';
import type { DiffTreeChange } from '@gitbook/util-tree-merge';
import { assertNever } from '@gitbook/util-ts';

import { DIFF_TEXT_COLORS } from '../../Diff';

/**
 * Display an icon indicating the type of change for a page.
 */
export function PageDiffIndicator(props: { pageDiff: DiffTreeChange<RevisionTreePage> }) {
    const { pageDiff } = props;
    const icon = getIconForDiffIndicator(pageDiff);
    const className = getClassNameForDiffIndicator(pageDiff);

    return (
        <Tooltip label={getTooltipTextForDiffIndicator(pageDiff)}>
            <div className="flex shrink-0">
                {renderIcon(icon, {
                    size: 14,
                    className: clsx(className),
                })}
            </div>
        </Tooltip>
    );
}

/**
 * Get the icon for the diff indicator.
 */
function getIconForDiffIndicator(
    pageDiff: DiffTreeChange<RevisionTreePage>
): IconDefinition | undefined {
    const type = pageDiff.type;
    switch (type) {
        case 'added':
            return Icon.DiffAdded;
        case 'modified':
            return Icon.DiffModified;
        case 'deleted':
            return Icon.DiffRemoved;
        default:
            assertNever(type);
    }
}

/**
 * Get the style for the diff indicator.
 */
function getClassNameForDiffIndicator(
    pageDiff: DiffTreeChange<RevisionTreePage>
): string | undefined {
    const baseClass = 'size-5 opacity-50';
    switch (pageDiff.type) {
        case 'added':
        case 'modified':
        case 'deleted':
            return clsx(baseClass, DIFF_TEXT_COLORS[pageDiff.type]);
        default:
            return undefined;
    }
}

/**
 * Get text for the tooltip of the diff indicator.
 */
function getTooltipTextForDiffIndicator(
    pageDiff: DiffTreeChange<RevisionTreePage>
): string | undefined {
    const type = pageDiff.type;
    switch (type) {
        case 'added':
            return 'This page has been added';
        case 'modified':
            return 'This page has been modified';
        case 'deleted':
            return 'This page has been deleted';
        default:
            assertNever(type);
    }
}
